import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { AuthService } from '../../root/services/auth.service';
import { ToastService } from '../../shared/services';
import { UserService } from '../../root/services/user.service';
import { catchError, finalize, mapTo, switchMap, take, tap } from 'rxjs/operators';
import { TermsAndConditionsService } from '../../root/services/terms-and-conditions.service';
import { SignUpTimelineService } from './sign-up-timeline.service';
import { Router } from '@angular/router';
import { CommunicationPreferencesService } from '../../root/services/communication-preferences.service';
import { OnboardingService } from '../../root/services/onboarding.service';
import { OnboardingStatus } from '../../shared/model/onboarding-status.dto';
import { CommsPreferencesDTO } from '../../shared/model/communication-preferences.dto';
import { NavigationService } from '../../root/services/navigation.service';
import { SIGN_UP_STEP } from '../../shared/model/timeline.dto';

@Injectable({
  providedIn: 'root'
})
export class SignUpStateService {
  private readonly passwordSet = new BehaviorSubject<string | undefined>(undefined);
  private readonly declineTermsAndConditions = new BehaviorSubject<boolean | undefined>(undefined);
  private readonly tokenId = new BehaviorSubject<string | undefined>(undefined);
  private readonly applicantId = new BehaviorSubject<string | undefined>(undefined);
  private readonly termsAndConditionsId = new BehaviorSubject<string | undefined>(undefined);
  private readonly currentStep = new BehaviorSubject<number | null>(null);
  private isSubmitting = new BehaviorSubject<boolean>(false);

  public updateIsSubmitting$ = this.isSubmitting;
  public termsAndConditionsAreDeclined$ = this.declineTermsAndConditions;
  public userTokenId$ = this.tokenId;
  public password$ = this.passwordSet;
  public userApplicantId$ = this.applicantId;
  public termsAndConditionsId$ = this.termsAndConditionsId;
  public getCurrentStep$ = this.currentStep;

  constructor(
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly termsAndConditionsService: TermsAndConditionsService,
    private readonly toastService: ToastService,
    private readonly signUpTimelineService: SignUpTimelineService,
    private readonly commsPreferencesService: CommunicationPreferencesService,
    private readonly navigationService: NavigationService,
    private readonly onboardingService: OnboardingService,
    private router: Router
  ) {}

  public initialize(): Observable<void> {
    if (this.applicantId.value) {
      return of(void 0);
    }

    return this.authService.checkSignedObservable().pipe(
      tap((user) => {
        this.userService.setUser(user);
        this.applicantId.next(user.id);
      }),
      switchMap(() => this.onboardingService.getStatus()),
      tap((status) => {
        // users who finished the wizard never resume it - straight to the dashboard
        if (status?.completed) {
          this.completeOnboarding();
          return;
        }

        if (status?.mobileVerified) {
          this.signUpTimelineService.markStepComplete(SIGN_UP_STEP.SECURITY);
        }

        // terms step is complete only if the user has accepted the current published version
        if (status?.termsAcceptedCurrent) {
          this.signUpTimelineService.markStepComplete(SIGN_UP_STEP.TERMS);
        }

        const highestStep = status?.termsAcceptedCurrent
          ? SIGN_UP_STEP.PREFERENCES
          : status?.mobileVerified
            ? SIGN_UP_STEP.TERMS
            : SIGN_UP_STEP.SECURITY;

        if (this.currentStep.value == null || this.currentStep.value < highestStep) {
          this.currentStep.next(highestStep);
        }
      }),
      mapTo(void 0),
      catchError((error) => {
        console.error('[SignUpStateService] User not signed in or error occurred:', error);
        this.navigationService.goToLogin().then(); // redirect
        return of(void 0); // continue observable chain
      })
    );
  }

  // After a step is saved, skip the remaining wizard if onboarding is already complete
  // (e.g. re-accepting stale T&Cs when comms preferences were set long ago).
  // Returns an observable that completes once the step has advanced (or the user
  // has been redirected to the dashboard) so callers can defer clearing their
  // "submitting" state until the async status check actually resolves.
  private advanceOrFinish(nextStep: number): Observable<void> {
    return this.onboardingService.getStatus().pipe(
      take(1),
      // if the status check fails, keep the wizard moving rather than stranding the user
      catchError(() => of(null)),
      tap((status: OnboardingStatus | null) => {
        if (status?.completed) {
          this.completeOnboarding();
          return;
        }

        this.signUpTimelineService.markStepComplete(nextStep - 1);
        this.currentStep.next(nextStep);
      }),
      mapTo(void 0)
    );
  }

  private completeOnboarding(): void {
    this.onboardingService.markCompleted();
    this.router.navigate(['/dashboard']);
  }

  public setPassword(passwordValue: string): void {
    const token = this.tokenId.value;

    if (!token) {
      this.toastService.add(
        { header: 'Your invite link is missing or has expired.', message: 'Please use the link from your email.' },
        'info'
      );
      return;
    }

    this.updateIsSubmitting$.next(true);

    this.authService
      .setPasswordObservable(token, passwordValue)
      .pipe(
        tap((session) => this.userService.setUser(session)),
        catchError((error) => {
          const msg = error?.error?.error?.message ?? 'Unknown error occurred.';
          this.toastService.add({ header: `Could not set password.`, message: `${msg}` }, 'error');
          return throwError(() => error);
        }),
        // only advance the wizard and re-run initialize() once the password
        // has actually been set; switchMap skips automatically when the
        // upstream errors out, so the step marker isn't advanced on failure
        switchMap(() => {
          this.signUpTimelineService.markStepComplete(SIGN_UP_STEP.PASSWORD);
          this.currentStep.next(SIGN_UP_STEP.SECURITY);
          return this.initialize();
        }),
        finalize(() => this.updateIsSubmitting$.next(false))
      )
      .subscribe();
  }

  // The mobile verification itself is done via the API in the mobile authorisation
  // component; this just advances the wizard, re-checking completion server-side
  public setTwoFactorAuthentication(): void {
    this.updateIsSubmitting$.next(true);
    this.advanceOrFinish(SIGN_UP_STEP.TERMS)
      .pipe(finalize(() => this.updateIsSubmitting$.next(false)))
      .subscribe();
  }

  public acceptTermsAndConditions(): void {
    const termsAndConditionsId = this.termsAndConditionsId.value;

    if (!termsAndConditionsId) {
      this.toastService.add({ header: 'Terms and conditions are still loading.', message: 'Please try again.' });
      return;
    }

    this.updateIsSubmitting$.next(true);

    this.termsAndConditionsService
      .acceptTermsAndConditions(termsAndConditionsId)
      .pipe(
        catchError((error) => {
          const msg = error?.error?.error?.message ?? 'Unknown error occurred.';
          this.toastService.add({ header: `Could not accept terms and conditions.`, message: `${msg}` }, 'error');
          return throwError(() => error);
        }),
        // only advance the wizard once the terms acceptance has actually landed;
        // switchMap skips automatically when the upstream errors out
        switchMap(() => this.advanceOrFinish(SIGN_UP_STEP.PREFERENCES)),
        finalize(() => this.updateIsSubmitting$.next(false))
      )
      .subscribe();
  }

  public setCommsPreferences(commsPreferenceData: CommsPreferencesDTO) {
    let hasError = false;
    this.commsPreferencesService
      .setCommsPreferences(commsPreferenceData)
      .pipe(
        catchError((errorCode) => {
          hasError = true;
          if (errorCode.status === 400) {
            this.toastService.add({ header: 'Please check your input and try again.' }, 'error', errorCode);
          } else if (errorCode.status === 404) {
            this.toastService.add(
              { header: "We couldn't find your details.", message: 'Please try again later.' },
              'error',
              errorCode
            );
          } else if (errorCode.status === 500) {
            this.toastService.add(
              { header: 'Something went wrong on our side.', message: 'Please try again later.' },
              'error',
              errorCode
            );
          } else {
            // Unknown error fallback
            this.toastService.add({ header: 'An unexpected error occurred.' }, 'error', errorCode);
          }

          return of(null);
        }),
        tap(() => {
          if (!hasError) {
            // comms preferences is the final wizard step - onboarding is now complete
            this.signUpTimelineService.markStepComplete(SIGN_UP_STEP.PREFERENCES);
            this.completeOnboarding();
          }
        })
      )
      .subscribe();
  }
}
